import type { DynamicClient } from '@dynamic-labs-sdk/client';
import {
  type WalletAccount,
  getWalletAccounts,
} from '@dynamic-labs-sdk/client';
import {
  WalletProviderPriority,
  createVisit,
  emitWalletAccountsChangedEvent,
  getWalletProviderRegistry,
  setUnverifiedWalletAccounts,
  updateWalletProviderKeysForVerifiedCredentials,
} from '@dynamic-labs-sdk/client/core';

import type { WalletProviderBuilder } from '../walletConnectWalletProvider.types';
import { createWalletAccountsForWalletConnectWalletProvider } from '../createWalletAccountsForWalletConnectWalletProvider';

type ImportWalletConnectSessionParams = {
  /**
   * Whether this session's wallet accounts should be added to the dynamic's wallet accounts.
   */
  addToDynamicWalletAccounts?: boolean;

  /**
   * The session topic of the WalletConnect session.
   */
  sessionTopic: string;

  /**
   * The function that builds the wallet provider for the session.
   */
  walletProviderBuilder: WalletProviderBuilder;
};

/**
 * This is a lower level function that is used to connect a WalletConnect session to the Dynamic SDK.
 * It will generate the wallet providers and wallet accounts for all accounts in the session.
 *
 * @param sessionTopic - The session topic of the WalletConnect session.
 * @param addToDynamicWalletAccounts - Whether to add the wallet accounts to the dynamic's wallet accounts.
 * @param client - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns The wallet accounts.
 */
export const importWalletConnectSession = async (
  {
    addToDynamicWalletAccounts = true,
    sessionTopic,
    walletProviderBuilder,
  }: ImportWalletConnectSessionParams,
  client: DynamicClient
): Promise<{ walletAccounts: WalletAccount[] }> => {
  /**
   * You would normally get the session from the signClient.connect method. It takes the optionalNamespaces as an argument.
   *
   * Each chain should build its own "connectWithWalletConnect<chain-name>" function.
   * Understand how to build the optionalNamespaces object:
   *
   * The namespace configurations are used at the time a wallet application receives the connection URI.
   * It will use the configurations we supply to understand which methods, events and networks it should provide.
   *
   * The wallet application will then accept the connection request and, in the session`object, provide its own namespace object
   * in return, which will contain the methods, events and networks it does in fact support, as well as
   * which accounts are available.
   *
   * WalletConnect expects namespaces defined in the following format:
   * ```typescript
   * {
   *   <namespaceKey>: {
   *     "accounts": ["<account1>", "<account2>", "<account3>", ...],
   *     "events": ["accountsChanged", "chainChanged", "disconnected"],
   *     "methods": ["personal_sign", "eth_sendTransaction", "eth_accounts", ...],
   *     "chains": ["<chain1>", "<chain2>", "<chain3>", ...]
   *   },
   *   ...
   * }
   * ```
   *
   * Where each account is a CAIP-10 formatted string, for example: "eip155:1:0x1234567890"
   * — see docs: https://chainagnostic.org/CAIPs/caip-10
   *
   * Where each chain is a CAIP-2 formatted string, for example: "eip155:1"
   * — see docs: https://chainagnostic.org/CAIPs/caip-2
   */
  const walletProviderRegistry = getWalletProviderRegistry(client);

  const walletProvider = await walletProviderBuilder({ sessionTopic }, client);

  walletProviderRegistry.register({
    priority: WalletProviderPriority.WALLET_SDK,
    walletProvider,
  });

  const walletAccounts = createWalletAccountsForWalletConnectWalletProvider({
    walletProvider,
  });

  const existingWalletAccounts = getWalletAccounts(client);

  /**
   * Keep track of which of the new wallet accounts are unverified to add later.
   */
  const unverifiedWalletAccounts: {
    isNew: boolean;
    walletAccount: WalletAccount;
  }[] = [];

  let walletAccountsChanged = false;

  /**
   * For all verified wallet accounts that are tied to this session, we need to update
   * the map of verified credential to wallet provider key
   */
  const verifiedCredentialIdToWalletProviderKeyMap: Record<string, string> = {};

  walletAccounts.forEach((walletAccount) => {
    const matchingWalletAccount = existingWalletAccounts.find(
      (existingWalletAccount) => existingWalletAccount.id === walletAccount.id
    );

    const matchingVerifiedCredentialId =
      matchingWalletAccount?.verifiedCredentialId;

    if (!matchingVerifiedCredentialId) {
      unverifiedWalletAccounts.push({
        isNew: !matchingWalletAccount,
        walletAccount,
      });
    } else {
      // This ensures later calls to getWalletAccounts will reconstruct the wallet accounts with
      // the correct wallet provider key
      verifiedCredentialIdToWalletProviderKeyMap[matchingVerifiedCredentialId] =
        walletProvider.key;

      // This ensures the wallet accounts returned by this function have the correct verified credential id
      walletAccount.verifiedCredentialId = matchingVerifiedCredentialId;
    }
  });

  if (Object.keys(verifiedCredentialIdToWalletProviderKeyMap).length > 0) {
    updateWalletProviderKeysForVerifiedCredentials(
      { keysToUpdate: verifiedCredentialIdToWalletProviderKeyMap },
      client
    );

    walletAccountsChanged = true;
  }

  if (addToDynamicWalletAccounts && unverifiedWalletAccounts.length > 0) {
    walletAccountsChanged = true;

    const unverifiedWalletAccountsToUpdate = unverifiedWalletAccounts.map(
      ({ walletAccount }) => walletAccount
    );

    setUnverifiedWalletAccounts({ unverifiedWalletAccountsToUpdate }, client);

    const newWalletAccounts = unverifiedWalletAccounts
      .filter(({ isNew }) => isNew)
      .map(({ walletAccount }) => walletAccount);

    for (const walletAccount of newWalletAccounts) {
      void createVisit({ walletAccount }, client);
    }
  }

  if (walletAccountsChanged) {
    emitWalletAccountsChangedEvent(client);
  }

  return { walletAccounts };
};
